/* ==========================================================================
   ApeLabs theme behaviour
   Loaded as a module so it can talk to Horizon's cart drawer through the
   storefront standard events (same contract product-form.js uses).
   ========================================================================== */
import { CartLinesUpdateEvent } from '@shopify/events';

const config = window.ApeConfig || {};
const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const $ = (sel, ctx = document) => ctx.querySelector(sel);
const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

/* ---------- Money ---------- */
function formatMoney(cents, format = config.moneyFormat || '₹{{amount}}') {
  const value = Number(cents || 0) / 100;
  const group = (num, decimals, thousands, decimal) => {
    const fixed = num.toFixed(decimals);
    const [int, frac] = fixed.split('.');
    const withSep = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return frac && Number(frac) !== 0 ? withSep + decimal + frac : withSep;
  };
  return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount_no_decimals':
        return group(value, 0, ',', '.');
      case 'amount_with_comma_separator':
        return group(value, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator':
        return group(value, 0, '.', ',');
      case 'amount_with_space_separator':
        return group(value, 2, ' ', ',');
      case 'amount_no_decimals_with_space_separator':
        return group(value, 0, ' ', ',');
      case 'amount_with_apostrophe_separator':
        return group(value, 2, "'", '.');
      default:
        return group(value, 2, ',', '.');
    }
  });
}

/* ---------- Checkout ----------
   The store checks out through GoKwik, not Shopify's native checkout. Their app
   embed exposes triggerGokwikCustomCheckout(), which opens GoKwik with whatever
   is in the cart. If the embed is missing (app disabled, or previewing without
   it) we fall back to the native checkout so the button is never a dead end. */
function goToCheckout() {
  try {
    if (typeof window.triggerGokwikCustomCheckout === 'function') {
      window.triggerGokwikCustomCheckout();
      return;
    }
  } catch (error) {
    /* fall through to the native checkout below */
  }
  window.location.href = `${root}checkout`;
}

/* ---------- Cart ---------- */
async function fetchCart() {
  const res = await fetch(`${root}cart.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
  return res.json();
}

function setCartCount(count) {
  $$('[data-ape-cart-count]').forEach((el) => {
    el.textContent = count;
  });
}

/**
 * Adds lines to the cart and lets Horizon's drawer/cart icon refresh themselves.
 * @param {{id: number|string, quantity?: number, properties?: object, selling_plan?: string}[]} items
 * @param {EventTarget} [origin]
 */
async function addItems(items, origin = document, options = {}) {
  const lines = items.filter((item) => item && item.id);
  if (!lines.length) throw new Error('Nothing to add');

  const sectionIds = $$('cart-items-component')
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);
  const itemCount = lines.reduce((sum, line) => sum + (Number(line.quantity) || 1), 0);
  const deferred = CartLinesUpdateEvent.createPromise();

  origin.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: lines.map((line) => ({ merchandiseId: String(line.id), quantity: Number(line.quantity) || 1 })),
      promise: deferred.promise,
    })
  );

  let response;
  try {
    const res = await fetch((window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || `${root}cart/add.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: lines.map((line) => {
          const payload = { id: Number(line.id), quantity: Number(line.quantity) || 1 };
          if (line.properties && Object.keys(line.properties).length) payload.properties = line.properties;
          if (line.selling_plan) payload.selling_plan = line.selling_plan;
          return payload;
        }),
        sections: sectionIds.join(','),
      }),
    });
    response = await res.json();
  } catch (error) {
    deferred.reject(error);
    throw error;
  }

  const cart = await fetchCart();
  const didError = Boolean(response && response.status);
  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
    detail: {
      didError,
      items: cart.items,
      itemCount,
      source: 'ape-theme',
      sections: didError ? undefined : response.sections,
    },
  });
  setCartCount(cart.item_count);

  if (didError) throw new Error(response.description || response.message || 'Could not add to cart');

  // Cart set to "page" instead of drawer: go to the cart.
  if (!document.getElementById('cart-drawer')) {
    window.location.href = `${root}cart`;
    return cart;
  }

  // Show the cart. Done here rather than relying on the theme's auto-open
  // setting, so adding always gives visible feedback. Skipped for "buy now",
  // which is heading straight to checkout.
  if (options.openDrawer !== false) openCart();
  return cart;
}

function openCart() {
  const drawer = document.getElementById('cart-drawer');
  if (drawer && typeof drawer.open === 'function') {
    drawer.open();
    return true;
  }
  return false;
}

function flash(button, text, ms = 1600) {
  if (!button) return;
  const label = button.querySelector('[data-ape-label]') || button;
  const original = label.dataset.original || label.textContent;
  label.dataset.original = original;
  label.textContent = text;
  setTimeout(() => {
    label.textContent = label.dataset.original;
  }, ms);
}

window.Ape = Object.assign(window.Ape || {}, { addItems, openCart, formatMoney, fetchCart });

/* ---------- Global cart count sync (any app/theme cart change) ---------- */
document.addEventListener('shopify:cart:lines-update', (event) => {
  if (!event.promise) return;
  event.promise
    .then(async (result) => {
      const qty = result && result.cart && typeof result.cart.totalQuantity === 'number' ? result.cart.totalQuantity : null;
      if (qty !== null) setCartCount(qty);
      else setCartCount((await fetchCart()).item_count);
    })
    .catch(() => {});
});

/* ---------- Plain cart/add forms (collection cards) ----------
   The collection cards post a normal HTML form to /cart/add, which reloads the
   page and never opens the drawer. Intercept those and add over Ajax instead.
   Scoped to forms the theme's own components don't already handle. */
document.addEventListener('submit', async (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) return;
  const action = form.getAttribute('action') || '';
  if (!action.includes('/cart/add')) return;
  if (form.closest('ape-product, product-form-component, quick-add-component')) return;
  if (form.dataset.apeBusy) return;

  const data = new FormData(form);
  const id = data.get('id');
  if (!id) return;

  event.preventDefault();
  form.dataset.apeBusy = 'true';
  const button = form.querySelector('[type="submit"]');
  if (button) button.classList.add('is-loading');
  try {
    await addItems([{ id, quantity: Number(data.get('quantity')) || 1 }], form);
  } catch (error) {
    flash(button, 'Sold out', 2200);
  } finally {
    delete form.dataset.apeBusy;
    if (button) button.classList.remove('is-loading');
  }
});

/* ---------- Delegated clicks ---------- */
document.addEventListener('click', async (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  // Quick add
  const addBtn = target.closest('[data-ape-add]');
  if (addBtn) {
    event.preventDefault();
    if (addBtn.classList.contains('is-loading')) return;
    addBtn.classList.add('is-loading');
    try {
      await addItems([{ id: addBtn.dataset.apeAdd, quantity: Number(addBtn.dataset.quantity) || 1 }], addBtn, {
        openDrawer: !addBtn.hasAttribute('data-buy-now'),
      });
      if (addBtn.hasAttribute('data-buy-now')) {
        goToCheckout();
        return;
      }
      flash(addBtn, addBtn.dataset.addedText || 'Added ✓');
    } catch (error) {
      flash(addBtn, 'Sold out', 2200);
    } finally {
      addBtn.classList.remove('is-loading');
    }
    return;
  }

  // Checkout: the theme's own cart drawer is used, but checkout itself must go
  // through GoKwik rather than submitting the cart form to Shopify's checkout.
  const checkoutBtn = target.closest('[name="checkout"], #checkout, .cart__checkout-button, [data-ape-checkout]');
  if (checkoutBtn && !checkoutBtn.disabled) {
    if (typeof window.triggerGokwikCustomCheckout === 'function') {
      event.preventDefault();
      goToCheckout();
      return;
    }
  }

  // Cart drawer
  const cartBtn = target.closest('[data-ape-cart]');
  if (cartBtn && openCart()) {
    event.preventDefault();
    return;
  }

  // Search
  const searchBtn = target.closest('[data-ape-search]');
  if (searchBtn) {
    const modal = document.getElementById('search-modal');
    if (modal && typeof modal.showDialog === 'function') {
      event.preventDefault();
      modal.showDialog();
    }
    return;
  }

  // Mobile menu
  const burger = target.closest('[data-ape-burger]');
  if (burger) {
    const panel = document.getElementById(burger.getAttribute('aria-controls'));
    if (!panel) return;
    const open = burger.getAttribute('aria-expanded') !== 'true';
    burger.setAttribute('aria-expanded', String(open));
    panel.hidden = !open;
    return;
  }

  // Quiz openers (buttons or any link to #quiz)
  const quizOpener = target.closest('[data-ape-quiz-open], a[href$="#quiz"]');
  if (quizOpener) {
    const quiz = $('ape-quiz');
    if (quiz) {
      event.preventDefault();
      quiz.open();
      const menu = $('[data-ape-burger][aria-expanded="true"]');
      if (menu) menu.click();
    }
    return;
  }

  // Inline video play
  const play = target.closest('[data-ape-play]');
  if (play) {
    const holder = play.closest('[data-ape-video]');
    const video = holder && holder.querySelector('video');
    if (video) {
      event.preventDefault();
      holder.classList.add('is-playing');
      video.controls = true;
      video.muted = false;
      video.play().catch(() => {});
    }
  }
});

/* ---------- Header height var (used by sticky gallery) ---------- */
function syncHeaderHeight() {
  const header = $('.ape-header');
  if (!header) return;
  const group = header.closest('.shopify-section-group-header-group') || header;
  document.documentElement.style.setProperty('--ape-header-h', `${Math.round(group.getBoundingClientRect().height)}px`);
}
if (window.ResizeObserver) {
  const header = $('.ape-header');
  if (header) new ResizeObserver(syncHeaderHeight).observe(header);
}
syncHeaderHeight();

/* ---------- Autoplay muted videos in view ---------- */
if ('IntersectionObserver' in window) {
  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const video = entry.target;
        if (entry.isIntersecting) video.play().catch(() => {});
        else if (!video.controls) video.pause();
      });
    },
    { threshold: 0.35 }
  );
  $$('video[data-ape-autoplay]').forEach((video) => io.observe(video));
}

/* ==========================================================================
   Quiz
   ========================================================================== */
class ApeQuiz extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    try {
      this.data = JSON.parse(this.querySelector('script[type="application/json"]').textContent);
    } catch (e) {
      this.data = { questions: [], results: [] };
    }
    this.body = this.querySelector('[data-quiz-body]');
    this.querySelector('[data-quiz-close]')?.addEventListener('click', () => this.dialog.close());
    this.dialog?.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog.close();
    });
    if (window.location.hash === '#quiz') requestAnimationFrame(() => this.open());
  }

  open() {
    if (!this.dialog || !this.data.questions.length) return;
    this.step = 0;
    this.scores = [];
    this.render();
    if (!this.dialog.open) this.dialog.showModal();
  }

  render() {
    const { questions, results, labels = {} } = this.data;
    const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
    if (this.step < questions.length) {
      const q = questions[this.step];
      this.body.innerHTML = `
        <div class="ape-quiz__step">${esc(labels.step || 'Question')} ${this.step + 1} / ${questions.length}</div>
        <h2 class="ape-quiz__q">${esc(q.q)}</h2>
        <div class="ape-quiz__opts">
          ${q.options.map((opt, i) => `<button type="button" class="ape-quiz__opt" data-score="${i}">${esc(opt)}</button>`).join('')}
        </div>`;
      $$('[data-score]', this.body).forEach((btn) =>
        btn.addEventListener('click', () => {
          this.scores.push(Number(btn.dataset.score));
          this.step += 1;
          this.render();
        })
      );
      $('.ape-quiz__opt', this.body)?.focus();
      return;
    }

    const avg = this.scores.reduce((a, b) => a + b, 0) / Math.max(this.scores.length, 1);
    const index = Math.min(results.length - 1, Math.max(0, Math.round((avg / 2) * (results.length - 1))));
    const res = results[index] || results[0];
    if (!res) return;
    this.body.innerHTML = `
      <div class="ape-quiz__step">${esc(labels.match || 'Your match')}</div>
      <h2 class="ape-quiz__q">${esc(res.name)}</h2>
      <p class="ape-quiz__why">${esc(res.why)}</p>
      <div class="ape-quiz__res-actions">
        ${
          res.variant && res.available
            ? `<button type="button" class="ape-btn ape-btn--md" data-ape-add="${res.variant}" data-quiz-add><span data-ape-label>${esc(labels.add || 'Add to cart')} · ${esc(res.price)}</span></button>`
            : ''
        }
        <a class="ape-btn ape-btn--md ape-btn--soft" href="${esc(res.url)}">${esc(labels.view || 'View details')}</a>
        <button type="button" class="ape-btn ape-btn--md ape-btn--soft" data-quiz-restart>${esc(labels.retake || 'Retake')}</button>
      </div>`;
    $('[data-quiz-restart]', this.body)?.addEventListener('click', () => this.open());
    $('[data-quiz-add]', this.body)?.addEventListener('click', () => setTimeout(() => this.dialog.close(), 50));
  }
}
if (!customElements.get('ape-quiz')) customElements.define('ape-quiz', ApeQuiz);

/* ==========================================================================
   Product page
   ========================================================================== */
class ApeProduct extends HTMLElement {
  connectedCallback() {
    try {
      this.data = JSON.parse(this.querySelector('script[data-ape-product-json]').textContent);
    } catch (e) {
      return;
    }
    this.form = this.querySelector('form[data-ape-form]');
    this.optionInputs = $$('input[data-opt-index]', this);
    this.lowStock = Number(this.data.lowStock) || 20;
    this.labels = this.data.labels || {};

    this.optionInputs.forEach((input) => input.addEventListener('change', () => this.onOptionChange()));
    this.form?.addEventListener('submit', (event) => this.onSubmit(event));

    this.initGallery();
    this.initSticky();
    this.initPincode();

    this.current = this.data.variants.find((v) => v.id === this.data.selected) || this.data.variants[0];
    this.refreshChips();

    // The gallery always opens on the first image and swipes in order. It only
    // starts on a variant's image when the link asked for that variant
    // (?variant=, e.g. a colour dot on a product card); picking a colour on the
    // page jumps to its image through update().
    const askedForVariant = new URLSearchParams(window.location.search).has('variant');
    if (askedForVariant && this.current && this.current.media && this.slides) {
      const start = this.slides.findIndex((slide) => slide.dataset.mediaId === String(this.current.media));
      if (start > 0) {
        requestAnimationFrame(() => {
          this.track.style.scrollBehavior = 'auto';
          this.track.scrollLeft = start * this.track.clientWidth;
          this.track.style.scrollBehavior = '';
          this.setIndex(start);
        });
      }
    }
  }

  selectedOptions() {
    const count = this.data.optionCount;
    const selected = [];
    for (let i = 0; i < count; i += 1) {
      const checked = this.optionInputs.find((input) => Number(input.dataset.optIndex) === i && input.checked);
      selected.push(checked ? checked.value : null);
    }
    return selected;
  }

  findVariant(options) {
    return this.data.variants.find((variant) => variant.options.every((value, i) => options[i] === null || value === options[i]));
  }

  onOptionChange() {
    const options = this.selectedOptions();
    let variant = this.data.variants.find((v) => v.options.every((value, i) => value === options[i]));
    if (!variant) {
      // Keep the value the shopper just picked and fall back to the closest available combination.
      variant = this.data.variants.find((v) => v.available && v.options[0] === options[0]) || this.findVariant([options[0]]);
      if (variant) {
        variant.options.forEach((value, i) => {
          const input = this.optionInputs.find((el) => Number(el.dataset.optIndex) === i && el.value === value);
          if (input) input.checked = true;
        });
      }
    }
    this.current = variant;
    this.update();
  }

  update() {
    const v = this.current;
    const unavailable = !v;
    $$('[data-ape-variant-id]', this).forEach((input) => {
      input.value = v ? v.id : '';
    });

    this.optionInputs
      .filter((input) => input.checked)
      .forEach((input) => {
        const label = this.querySelector(`[data-ape-opt-name="${input.dataset.optIndex}"]`);
        if (label) label.textContent = input.value;
      });

    if (v) {
      const priceText = formatMoney(v.price);
      $$('[data-ape-price]', this).forEach((el) => (el.textContent = priceText));
      const hasCompare = v.compare && v.compare > v.price;
      $$('[data-ape-compare]', this).forEach((el) => {
        el.hidden = !hasCompare;
        if (hasCompare) el.textContent = formatMoney(v.compare);
      });
      $$('[data-ape-save]', this).forEach((el) => {
        el.hidden = !hasCompare;
        if (hasCompare) el.textContent = (this.labels.save || 'You save [amount]').replace('[amount]', formatMoney(v.compare - v.price));
      });
      $$('[data-ape-prepaid-price]', this).forEach((el) => {
        const pct = Number(el.dataset.percent) || 0;
        el.textContent = formatMoney(Math.round((v.price * (100 - pct)) / 100));
      });
    }

    const stockEl = this.querySelector('[data-ape-stock]');
    if (stockEl) {
      let text = '';
      if (!v) text = this.labels.unavailable || 'Unavailable';
      else if (!v.available) text = this.labels.soldOut || 'Sold out';
      else if (v.qty !== null && v.qty > 0 && v.qty < this.lowStock) text = (this.labels.low || 'Only [qty] left').replace('[qty]', v.qty);
      stockEl.textContent = text;
      stockEl.hidden = !text;
    }

    $$('[data-ape-atc], [data-ape-buynow]', document).forEach((btn) => {
      if (!this.contains(btn) && !btn.closest('[data-ape-sticky]')) return;
      const off = unavailable || !v.available;
      btn.disabled = off;
      const label = btn.querySelector('[data-ape-label]');
      if (!label) return;
      if (!label.dataset.original) label.dataset.original = label.textContent;
      label.textContent = off ? (unavailable ? this.labels.unavailable || 'Unavailable' : this.labels.soldOut || 'Sold out') : label.dataset.original;
    });

    if (v) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', v.id);
      window.history.replaceState({}, '', url.toString());
      if (v.media) this.goToMedia(v.media);
    }

    this.refreshChips();
    document.dispatchEvent(new CustomEvent('ape:variant-change', { detail: { variant: v, productId: this.data.id } }));
  }

  refreshChips() {
    const selected = this.selectedOptions();
    this.optionInputs.forEach((input) => {
      const index = Number(input.dataset.optIndex);
      const probe = selected.slice();
      probe[index] = input.value;
      const match = this.data.variants.find((v) => v.options.every((value, i) => value === probe[i]));
      const chip = input.closest('.ape-chip');
      if (!chip) return;
      const available = Boolean(match && match.available);
      chip.classList.toggle('is-unavailable', !available);
      const low = available && match.qty !== null && match.qty > 0 && match.qty < this.lowStock;
      chip.classList.toggle('is-low', low);
      const stock = chip.querySelector('.ape-chip__stock');
      if (stock && index === 0 && this.data.showChipStock) {
        // An empty inStock label means "say nothing" when there is plenty.
        stock.textContent = !match ? '' : !available ? this.labels.soldOut || 'Sold out' : low ? (this.labels.lowShort || '[qty] left').replace('[qty]', match.qty) : this.labels.inStock ?? 'In stock';
      }
    });
  }

  async onSubmit(event) {
    event.preventDefault();
    const submitter = event.submitter;
    const buyNow = Boolean((submitter && submitter.hasAttribute('data-ape-buynow')) || this.forceBuyNow);
    this.forceBuyNow = false;
    const formData = new FormData(this.form);
    const id = formData.get('id');
    const errorEl = this.querySelector('[data-ape-error]');
    if (errorEl) errorEl.hidden = true;
    if (!id) return;

    const properties = {};
    for (const [key, value] of formData.entries()) {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match && value !== '') properties[match[1]] = value;
    }

    const buttons = $$('[data-ape-atc], [data-ape-buynow]').filter((b) => this.contains(b) || b.closest('[data-ape-sticky]'));
    const busy = buyNow ? buttons.filter((b) => b.hasAttribute('data-ape-buynow')) : buttons.filter((b) => b.hasAttribute('data-ape-atc'));
    busy.forEach((b) => b.classList.add('is-loading'));

    try {
      await addItems(
        [
          {
            id,
            quantity: Number(formData.get('quantity')) || 1,
            properties,
            selling_plan: formData.get('selling_plan') || undefined,
          },
        ],
        this.form,
        { openDrawer: !buyNow }
      );
      if (buyNow) {
        goToCheckout();
        return;
      }
    } catch (error) {
      if (errorEl) {
        errorEl.textContent = error.message;
        errorEl.hidden = false;
      }
    } finally {
      busy.forEach((b) => b.classList.remove('is-loading'));
    }
  }

  /* Gallery */
  initGallery() {
    this.track = this.querySelector('[data-ape-track]');
    if (!this.track) return;
    this.slides = $$('[data-ape-slide]', this.track);
    this.thumbs = $$('[data-ape-thumb]', this);
    this.counter = this.querySelector('[data-ape-count]');

    this.thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => this.goTo(i)));
    this.querySelector('[data-ape-prev]')?.addEventListener('click', () => this.goTo(this.index - 1));
    this.querySelector('[data-ape-next]')?.addEventListener('click', () => this.goTo(this.index + 1));

    this.index = 0;

    // Gallery videos behave like GIFs: muted, looping, playing only while their
    // slide is current and the gallery is on screen. A tap pauses or resumes.
    // With reduced motion they wait for the shopper and get their controls back.
    this.videos = $$('video', this.track);
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.galleryVisible = true;
    this.videos.forEach((video) => {
      video.muted = true;
      if (this.reduceMotion) {
        video.controls = true;
        return;
      }
      video.addEventListener('click', () => {
        video.dataset.userPaused = video.paused ? '' : 'true';
        if (video.paused) video.play().catch(() => {});
        else video.pause();
      });
    });
    if (this.videos.length && 'IntersectionObserver' in window) {
      new IntersectionObserver(
        ([entry]) => {
          this.galleryVisible = entry.isIntersecting;
          this.syncVideos();
        },
        { threshold: 0.4 }
      ).observe(this.track);
    }

    let raf;
    this.track.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          const i = Math.round(this.track.scrollLeft / Math.max(this.track.clientWidth, 1));
          if (i !== this.index) this.setIndex(i);
        });
      },
      { passive: true }
    );
  }

  setIndex(i) {
    this.index = i;
    this.thumbs.forEach((thumb, t) => thumb.setAttribute('aria-current', String(t === i)));
    if (this.counter) this.counter.textContent = `${i + 1} / ${this.slides.length}`;
    this.slides.forEach((slide, s) => {
      // Arriving on a slide again starts its video afresh, even after a tap-pause.
      if (s !== i) slide.querySelectorAll('video').forEach((video) => (video.dataset.userPaused = ''));
    });
    this.syncVideos();
    const thumb = this.thumbs[i];
    if (thumb && thumb.parentElement.scrollWidth > thumb.parentElement.clientWidth) {
      thumb.parentElement.scrollTo({ left: thumb.offsetLeft - 20, behavior: 'smooth' });
    }
  }

  syncVideos() {
    if (!this.slides) return;
    this.slides.forEach((slide, s) => {
      slide.querySelectorAll('video').forEach((video) => {
        const shouldPlay = s === this.index && this.galleryVisible && !this.reduceMotion && video.dataset.userPaused !== 'true';
        if (shouldPlay) video.play().catch(() => {});
        else if (!video.paused) video.pause();
      });
    });
  }

  goTo(i) {
    if (!this.slides || !this.slides.length) return;
    const next = (i + this.slides.length) % this.slides.length;
    this.track.scrollTo({ left: next * this.track.clientWidth, behavior: 'smooth' });
    this.setIndex(next);
  }

  goToMedia(mediaId) {
    if (!this.slides) return;
    const i = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
    if (i >= 0) this.goTo(i);
  }

  /* Sticky bar */
  initSticky() {
    const bar = document.querySelector(`[data-ape-sticky="${this.dataset.section}"]`);
    const anchor = this.querySelector('[data-ape-buy-anchor]');
    if (!bar || !anchor || !('IntersectionObserver' in window)) return;
    const footer = document.querySelector('.ape-footer, footer');
    let pastButtons = false;
    let footerVisible = false;
    const sync = () => bar.classList.toggle('is-visible', pastButtons && !footerVisible);
    new IntersectionObserver(([entry]) => {
      pastButtons = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      sync();
    }).observe(anchor);
    if (footer) {
      new IntersectionObserver(([entry]) => {
        footerVisible = entry.isIntersecting;
        sync();
      }).observe(footer);
    }
    bar.addEventListener('click', (event) => {
      const btn = event.target.closest('[data-ape-sticky-submit]');
      if (!btn || !this.form) return;
      const wantsBuy = btn.dataset.apeStickySubmit === 'buy';
      let real = this.form.querySelector(wantsBuy ? '[data-ape-buynow]' : '[data-ape-atc]');
      if (!real && wantsBuy) {
        real = this.form.querySelector('[data-ape-atc]');
        this.forceBuyNow = true;
      }
      if (real) this.form.requestSubmit(real);
    });
  }

  /* Pincode estimate */
  initPincode() {
    const wrap = this.querySelector('[data-ape-pin]');
    if (!wrap) return;
    const input = wrap.querySelector('input');
    const button = wrap.querySelector('button');
    const result = wrap.querySelector('[data-ape-pin-result]');
    const min = Number(wrap.dataset.min) || 2;
    const max = Number(wrap.dataset.max) || 6;
    const fmt = (d) => d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
    const run = () => {
      const pin = (input.value || '').replace(/\D/g, '').slice(0, 6);
      input.value = pin;
      result.hidden = false;
      if (!/^[1-9]\d{5}$/.test(pin)) {
        result.textContent = wrap.dataset.invalid || 'Enter a valid 6-digit pincode';
        result.classList.remove('is-ok');
        return;
      }
      const now = Date.now();
      const from = new Date(now + min * 864e5);
      const to = new Date(now + max * 864e5);
      result.textContent = (wrap.dataset.template || 'Estimated delivery [from] – [to]').replace('[from]', fmt(from)).replace('[to]', fmt(to));
      result.classList.add('is-ok');
      try {
        localStorage.setItem('ape-pin', pin);
      } catch (e) {}
    };
    button?.addEventListener('click', run);
    input?.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        run();
      }
    });
    /* Typing only sanitises the field. The estimate is shown when the shopper
       asks for it — clicking Check or pressing Enter — never while typing. */
    input?.addEventListener('input', () => {
      input.value = input.value.replace(/\D/g, '').slice(0, 6);
    });
    /* A remembered pincode prefills the field but does not print a date until
       the shopper checks it, so the estimate is always something they asked for. */
    try {
      const saved = localStorage.getItem('ape-pin');
      if (saved) input.value = saved;
    } catch (e) {}
  }
}
if (!customElements.get('ape-product')) customElements.define('ape-product', ApeProduct);

/* ==========================================================================
   Customer photos strip (ape-reviews): tap a photo for a lightbox with the
   review it came from. Arrow keys and the side buttons step through photos.
   ========================================================================== */
class ApeReviewPhotos extends HTMLElement {
  connectedCallback() {
    this.items = $$('[data-photo]', this);
    this.dialog = this.querySelector('dialog');
    if (!this.dialog || !this.items.length) return;
    this.img = this.dialog.querySelector('[data-lb-img]');
    this.stars = this.dialog.querySelector('[data-lb-stars]');
    this.text = this.dialog.querySelector('[data-lb-text]');
    this.author = this.dialog.querySelector('[data-lb-author]');
    const many = this.items.length > 1;
    $$('[data-lb-prev], [data-lb-next]', this.dialog).forEach((btn) => (btn.hidden = !many));

    this.items.forEach((item, i) => item.addEventListener('click', () => this.show(i)));
    this.dialog.querySelector('[data-lb-close]')?.addEventListener('click', () => this.dialog.close());
    this.dialog.querySelector('[data-lb-prev]')?.addEventListener('click', () => this.show(this.index - 1));
    this.dialog.querySelector('[data-lb-next]')?.addEventListener('click', () => this.show(this.index + 1));
    // A click on the backdrop (the dialog itself, outside its content) closes it.
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog.close();
    });
    this.dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') this.show(this.index - 1);
      if (event.key === 'ArrowRight') this.show(this.index + 1);
    });
  }

  show(i) {
    const count = this.items.length;
    this.index = (i + count) % count;
    const data = this.items[this.index].dataset;
    const score = Math.max(0, Math.min(5, Number(data.score) || 0));
    this.img.src = data.full;
    this.img.alt = `Photo from ${data.author}'s review`;
    this.stars.textContent = '★'.repeat(score) + '☆'.repeat(5 - score);
    this.text.textContent = data.text || '';
    this.text.hidden = !data.text;
    this.author.textContent = data.author || '';
    if (!this.dialog.open) this.dialog.showModal();
  }
}
if (!customElements.get('ape-review-photos')) customElements.define('ape-review-photos', ApeReviewPhotos);

/* ==========================================================================
   Frequently bought together
   ========================================================================== */
class ApeFbt extends HTMLElement {
  connectedCallback() {
    this.main = { id: Number(this.dataset.mainId), price: Number(this.dataset.mainPrice), available: this.dataset.mainAvailable === 'true' };
    this.items = $$('[data-fbt-item]', this);
    this.totalEl = this.querySelector('[data-fbt-total]');
    this.cta = this.querySelector('[data-fbt-cta]');
    this.items.forEach((item) => {
      item.querySelector('input[type="checkbox"]')?.addEventListener('change', () => this.refresh());
      item.querySelector('select')?.addEventListener('change', () => this.refresh());
    });
    document.addEventListener('ape:variant-change', (event) => {
      const v = event.detail && event.detail.variant;
      if (!v || String(event.detail.productId) !== this.dataset.productId) return;
      this.main = { id: v.id, price: v.price, available: v.available };
      this.refresh();
    });
    this.cta?.addEventListener('click', () => this.add());
    this.refresh();
  }

  selected() {
    return this.items
      .map((item) => {
        const checkbox = item.querySelector('input[type="checkbox"]');
        const select = item.querySelector('select');
        const option = select ? select.selectedOptions[0] : null;
        const id = option ? option.value : item.dataset.variant;
        const price = option ? Number(option.dataset.price) : Number(item.dataset.price);
        item.classList.toggle('is-on', Boolean(checkbox && checkbox.checked));
        return checkbox && checkbox.checked && !checkbox.disabled ? { id, price } : null;
      })
      .filter(Boolean);
  }

  refresh() {
    const picked = this.selected();
    const lines = (this.main.available ? [this.main] : []).concat(picked);
    const total = lines.reduce((sum, line) => sum + line.price, 0);
    if (this.totalEl) this.totalEl.textContent = formatMoney(total);
    if (this.cta) {
      const label = this.cta.querySelector('[data-ape-label]');
      const template = lines.length === 1 ? this.dataset.labelOne : this.dataset.labelMany;
      if (label) label.textContent = (template || 'Add [count] items').replace('[count]', lines.length);
      this.cta.disabled = lines.length === 0;
    }
  }

  async add() {
    const picked = this.selected();
    const lines = (this.main.available ? [{ id: this.main.id, quantity: 1 }] : []).concat(picked.map((p) => ({ id: p.id, quantity: 1 })));
    if (!lines.length) return;
    this.cta.classList.add('is-loading');
    try {
      await addItems(lines, this);
    } catch (error) {
      flash(this.cta, error.message, 2600);
    } finally {
      this.cta.classList.remove('is-loading');
    }
  }
}
if (!customElements.get('ape-fbt')) customElements.define('ape-fbt', ApeFbt);

/* ==========================================================================
   Product recommendations (async section render)
   ========================================================================== */
$$('[data-ape-recs]').forEach(async (holder) => {
  const url = holder.dataset.apeRecs;
  if (!url) return;
  try {
    const html = await (await fetch(url)).text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector('[data-ape-recs-content]');
    const section = holder.closest('.shopify-section');
    if (fresh && fresh.children.length) {
      holder.replaceWith(fresh);
    } else if (section) {
      section.hidden = true;
    }
  } catch (e) {
    const section = holder.closest('.shopify-section');
    if (section) section.hidden = true;
  }
});

/* ==========================================================================
   Carousel dots — under every row that scrolls sideways on phones, so the
   rail reads as swipeable. Rows that sit as a grid (desktop, or the category
   row) get no dots. Dots are an indicator: swiping stays the interaction, so
   they are hidden from assistive tech and kept out of the tab order.
   ========================================================================== */
(() => {
  const SKIP = ['ape-row--cats', 'ape-row--grid-mobile'];
  const rows = new Set();

  function cardsOf(row) {
    return Array.from(row.children).filter((el) => el.getClientRects().length > 0);
  }

  function activeIndex(row, cards) {
    const start = cards[0].offsetLeft;
    const pos = row.scrollLeft;
    let best = 0;
    let bestDist = Infinity;
    cards.forEach((card, i) => {
      const dist = Math.abs(card.offsetLeft - start - pos);
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    });
    /* at the far right the last card may never reach the start edge */
    if (row.scrollLeft + row.clientWidth >= row.scrollWidth - 4) best = cards.length - 1;
    return best;
  }

  function render(row) {
    const dots = row.__apeDots;
    const cards = cardsOf(row);
    const scrolls = row.scrollWidth > row.clientWidth + 8 && getComputedStyle(row).display === 'flex';
    if (!scrolls || cards.length < 2) {
      dots.hidden = true;
      return;
    }
    dots.hidden = false;
    if (dots.children.length !== cards.length) {
      dots.textContent = '';
      cards.forEach((card, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'ape-dots__dot';
        b.tabIndex = -1;
        b.addEventListener('click', () => {
          row.scrollTo({ left: card.offsetLeft - cards[0].offsetLeft, behavior: 'smooth' });
        });
        dots.appendChild(b);
      });
    }
    const on = activeIndex(row, cards);
    Array.from(dots.children).forEach((b, i) => b.classList.toggle('is-active', i === on));
  }

  function attach(row) {
    if (rows.has(row) || SKIP.some((c) => row.classList.contains(c))) return;
    rows.add(row);
    const dots = document.createElement('div');
    dots.className = 'ape-dots';
    dots.setAttribute('aria-hidden', 'true');
    dots.hidden = true;
    row.insertAdjacentElement('afterend', dots);
    row.__apeDots = dots;

    let ticking = false;
    row.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          render(row);
        });
      },
      { passive: true }
    );
    /* recommendation rows fill in after load */
    new MutationObserver(() => render(row)).observe(row, { childList: true });
    if ('ResizeObserver' in window) new ResizeObserver(() => render(row)).observe(row);
    render(row);
  }

  function scan(root) {
    (root || document).querySelectorAll('.ape-row').forEach(attach);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan());
  else scan();
  window.addEventListener('load', () => rows.forEach(render));
  document.addEventListener('shopify:section:load', (e) => scan(e.target));
})();
